import React from 'react';
import { Box } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { ChunkErrorBoundary } from '@shared/components/ChunkErrorBoundary';
import { useIsWeddingMapEditorPreview } from '@shared/hooks/weddingMapPageVisibility';
import { EVENT_PAGE_SLUGS } from '@apps/guest/shared/constants/eventPageSlugs';
import { LazyWeddingMap } from '../../widgets/WeddingMap/WeddingMap.lazy';
import type { WeddingMapVariant } from '../../widgets/WeddingMap/WeddingMap.types';

type WeddingMapPageSectionProps = Readonly<{
  eventHandle: string;
  /**
   * `section` (the default) is the map beneath another page's content, which is
   * how Travel mounts it. `page` is the map as a page of its own — the `map`
   * EventPage — where it owns the column and so keeps its side list.
   */
  variant?: WeddingMapVariant;
}>;

/**
 * The Wedding Map as a section beneath a page's own content, or as its own page.
 *
 * As a section it belongs on Travel, because that is the page a guest already
 * opens to answer "where am I staying and how do I get there" — the map answers
 * the same question with one surface instead of a list of addresses.
 *
 * **With the flag off this renders `null` and nothing else happens.** The gate
 * sits above `LazyWeddingMap`, so the map's chunk is never fetched and its
 * query never fires; a page that mounts this is byte-identical to what it was
 * before for every event outside the rollout. That is why the flag is read here
 * and not inside the widget.
 *
 * The flag is read **with exposure tracking**, unlike `useIsMapPageVisible`,
 * which decides whether the nav lists a map page and deliberately records
 * nothing — that one runs for every guest of every event, map page or not.
 * This is a surface that actually shows the map, so the exposure belongs here.
 * Reading it again here is also what stops a guest outside the rollout reaching
 * the map by typing the URL after the nav entry has been hidden from them.
 *
 * The map *page* also renders in the map editor's preview, so a couple in the
 * editor's rollout sees their edits before guests get the map. Travel's
 * section does not: that is not the page the editor is showing.
 */
export const WeddingMapPageSection: React.FC<WeddingMapPageSectionProps> = ({ eventHandle, variant = 'section' }) => {
  const [isEnabled] = useIsFeatureEnabled('guestWeddingMapRollout');
  const isEditorPreview = useIsWeddingMapEditorPreview();
  const isShown = isEnabled || (variant === 'page' && isEditorPreview);

  // The heading's `tq` lives in the child, so a Travel page outside the
  // rollout does not resolve copy it will never render.
  return isShown ? (
    <WeddingMapSectionContent
      eventHandle={eventHandle}
      variant={variant}
    />
  ) : null;
};
WeddingMapPageSection.displayName = 'WeddingMapPageSection';

const WeddingMapSectionContent: React.FC<WeddingMapPageSectionProps> = ({ eventHandle, variant = 'section' }) => {
  const { tq } = useTranslation('guestSiteTravel');

  return (
    <Box
      width="100%"
      // As a page there is nothing above to be spaced away from — the layout's
      // own page padding already sits between the header and this.
      marginTop={variant === 'section' ? pxToRem(48) : undefined}
    >
      <ChunkErrorBoundary section="guest/widget/weddingMap">
        {/*
          As a `section` the host page's own content already owns the column, and
          the map's side list inside it would read as a page within a page. As a
          `page` the column is the map's, so it keeps the list — which is where
          the place names and the nightly rates are.
        */}
        <LazyWeddingMap
          eventHandle={eventHandle}
          variant={variant}
          heading={tq`Everywhere you need to be`}
        />
      </ChunkErrorBoundary>
    </Box>
  );
};
WeddingMapSectionContent.displayName = 'WeddingMapSectionContent';

/**
 * Whether a page is "the travel page".
 *
 * Two shapes reach here. Legacy events carry a typed `EventPageType.travel`
 * page; events built in the current editor carry a `custom` page whose slug is
 * `travel`. Checking only the type silently skipped every modern event.
 */
export const isTravelPageSlug = (pageSlug?: string | null): boolean => pageSlug === EVENT_PAGE_SLUGS.Travel;
